import { ActionBarSpacer } from "$components/action-bar/action-bar-spacer";
import { LoadingPage } from "$components/loading/loading-page";
import { PageWithRightHandDrawer } from "$components/page/page-with-righthand-drawer";
import {
  type Patient,
  RXDB_PATIENT_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/patient";
import { useRxDBContext } from "$global-providers/rxdb-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { linkToHome } from "$routes/_lib/links";
import NavigateHome from "$routes/_lib/navigate-home";
import { useQuerystring } from "$routes/_lib/use-querystring";
import { PatientOverview } from "$routes/patient/[:patientId]/_lib/patient-overview/patient-overview";
import { PatientHistoryAndInsuranceSidebar } from "$routes/patient/[:patientId]/overview/_lib/history-and-insurance-sidebar";
import { styled } from "@material-ui/core";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useRxData } from "rxdb-hooks";
import { PatientDetailsActionBar } from "../_lib/patient-details-action-bar";

const Drawer = styled(PatientHistoryAndInsuranceSidebar)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: "800px",
}));

const Content = styled("div")(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(2, 3, 3),
  },
  width: "100%",
}));

const Overview = styled(PatientOverview)(() => ({
  width: "100%",
}));

export const PatientDetailsPage = () => {
  const { patientUuid: patientId } = useQuerystring();

  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const {
    replicationState: {
      patient: { isPulling: isPullingPatient },
    },
  } = useRxDBContext();

  const { result: patientData, isFetching: isFetchingPatientFromRxDb } =
    useRxData<Patient>(RXDB_PATIENT_COLLECTION_NAME, (collection) =>
      collection?.findOne({
        selector: { id: patientId },
      }),
    );
  const patient = patientData[0];

  useEffect(() => {
    if (!patient && !(isPullingPatient || isFetchingPatientFromRxDb)) {
      toast.error(
        formatMessage({
          id: "patientDetails.noPatientFound.error",
          defaultMessage: "Patient not found",
        }),
      );
      navigate(linkToHome());
    }
  }, [
    patient,
    isPullingPatient,
    isFetchingPatientFromRxDb,
    formatMessage,
    navigate,
  ]);

  if (!patientId) {
    toast.error("No user selected.");
    return <NavigateHome />;
  }

  if (!patient && (isPullingPatient || isFetchingPatientFromRxDb)) {
    return <LoadingPage />;
  }

  return (
    !!patient && (
      <PageWithRightHandDrawer
        data-testid="patientDetailsPageContainer"
        drawerContent={
          <>
            <Drawer patient={patient} />
            <ActionBarSpacer />
          </>
        }
      >
        <Content>
          <Overview patient={patient} />
          <ActionBarSpacer />
        </Content>
        <PatientDetailsActionBar patientId={patientId} />
      </PageWithRightHandDrawer>
    )
  );
};
